<template>
  <div class="course-step-form mx-auto my-6">
    <bh-text-input
      class="mt-2"
      label="Title"
      input-background-class="bhGray6"
      :value="title"
      @input-text="updateField('title', $event)"
      :counter="255"
      :show-counter-in-range="10"
      :force-rules="forceRules"
      :rules="[
        value => !!value || 'Required.',
        value => (value || '').length <= 255 || 'Max 255 characters',
      ]"
    />
    <bh-text-area
      :value="description"
      :counter="30000"
      :show-counter-in-range="10"
      class="mt-2 course-step-form__description"
      label="Description"
      @input-text="updateField('description', $event)"
      :validate="true"
      :force-rules="forceRules"
      :rules="[
        value => !!value || 'Required.',
        value => (value || '').length <= 30000 || 'Max 30,000 characters',
      ]"
    />
    <bh-text-input
      class="mt-2"
      label="Optional link"
      input-background-class="bhGray6"
      :value="link"
      @input-text="updateField('link', $event)"
      :rules="linkRules"
    />
    <thumbnail-picker
      label="Thumbnail"
      :current-file="currentFile"
      :current-mobile-static-media="currentMobileStaticMedia"
      @thumbnail-picked="setThumbnail"
      :allowed-file-types="[18]"
      :show-action="true"
      one-source-only
      enable-mobile-static-media
    />
    <thumbnail-picker
      label="Attachment"
      :current-file="currentAttachmentFile"
      @thumbnail-picked="setAttachment"
      :allowed-file-types="[18, 19, 20]"
      :show-action="true"
      one-source-only
    />
  </div>
</template>

<script>
import ThumbnailPicker from '@/components/inputs/ThumbnailPicker.vue'
import BhTextInput from '@/components/inputs/BhTextInput.vue'
import BhTextArea from '@/components/inputs/BhTextArea.vue'
import urlValidation from '@/helpers/urlValidation.js'
import { getFileTypeByUrl } from '@/helpers/chat'

export default {
  name: 'CourseStepForm',
  components: {
    ThumbnailPicker,
    BhTextInput,
    BhTextArea,
  },
  mixins: [],
  props: {
    readonly: {
      type: Boolean,
      default: false,
    },
    courseStep: {
      type: Object,
    },
    forceRules: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      title: '',
      description: '',
      link: '',
      thumbnailUrl: null,
      mediaUrl: null,
      thumbnailValue: null,
      id: null,
      staticMediaThumbnailUrl: null,
      attachmentUrl: null,
      attachmentValue: null,
    }
  },
  computed: {
    linkRules() {
      return [
        value => {
          if (!value || value === '') return true

          return this.validWebUrl(value || '') || 'Invalid URL'
        },
      ]
    },
    currentFile() {
      if (this.staticMediaThumbnailUrl) {
        return null
      }
      if (!this.thumbnailUrl && !this.mediaUrl) {
        return null
      }

      const { type } = getFileTypeByUrl(this.thumbnailUrl || this.mediaUrl)
      const fileTypeId = type === 'image' ? 18 : type === 'video' ? 19 : 20

      return {
        thumbnailUrl: this.thumbnailUrl || null,
        mediaUrl: this.mediaUrl || null,
        fileTypeId: fileTypeId || null,
      }
    },
    currentMobileStaticMedia() {
      if (!this.staticMediaThumbnailUrl) {
        return null
      }
      return {
        mediaUrl: this.staticMediaThumbnailUrl,
      }
    },
    currentAttachmentFile() {
      if (!this.attachmentUrl) {
        return null
      }

      const { type } = getFileTypeByUrl(this.attachmentUrl)
      const fileTypeId = type === 'image' ? 18 : type === 'video' ? 19 : 20

      return {
        thumbnailUrl:
          type === 'image' && this.attachmentUrl ? this.attachmentUrl : null,
        mediaUrl: this.attachmentUrl || null,
        fileTypeId: fileTypeId || null,
      }
    },
  },
  mounted() {
    if (this.courseStep) {
      const {
        title,
        description,
        link,
        thumbnailUrl,
        id,
        mediaUrl,
        staticMediaThumbnailUrl,
        attachmentUrl,
      } = this.courseStep
      this.title = title || ''
      this.description = description || ''
      this.link = link || ''
      this.id = id
      this.mediaUrl = mediaUrl || null
      this.thumbnailUrl = thumbnailUrl || null
      this.attachmentUrl = attachmentUrl || null
      this.staticMediaThumbnailUrl = staticMediaThumbnailUrl || null
      this.thumbnailValue = null
    }
  },
  methods: {
    validWebUrl: urlValidation.validWebUrl,
    setThumbnail(thumbnail = null) {
      if (thumbnail === null) {
        // remove
        this.thumbnailValue = { remove: true }
      } else {
        const { type, file } = thumbnail

        if (type === 'media-library') {
          this.thumbnailValue = { mlSourceResourceId: file.id }
        } else if (type === 'file-picker') {
          this.thumbnailUrl = null
          this.mediaUrl = null
          this.thumbnailValue = file
        } else if (type === 'mobile-static-media') {
          this.thumbnailValue = { staticMediaId: file.id }
        }
      }
    },
    setAttachment(thumbnail = null) {
      if (thumbnail === null) {
        // remove
        this.attachmentValue = { remove: true }
      } else {
        const { type, file } = thumbnail

        if (type === 'media-library') {
          this.attachmentValue = { mlSourceResourceId: file.id }
        } else if (type === 'file-picker') {
          this.attachmentUrl = null
          this.attachmentValue = file
        } else if (type === 'mobile-static-media') {
          this.attachmentValue = { staticMediaId: file.id }
        }
      }
    },
    updateField(fieldName, value) {
      const availableUpdateFields = [
        'title',
        'description',
        'link',
        'thumbnailValue',
        'attachmentValue',
      ]
      if (availableUpdateFields.includes(fieldName)) {
        this[fieldName] = value
      }
    },
    getData() {
      return {
        title: this.title,
        description: this.description,
        link: this.link,
        id: this.id,
        file: this.thumbnailValue,
        attachmentFile: this.attachmentValue,
      }
    },
  },
  watch: {},
}
</script>

<style lang="scss">
@import '@/styles/variables.scss';

.course-step-form {
  &__description {
    .bh-text-area__input {
      flex: initial;
    }
    label.bh-text-area__label {
      color: $bh-gray--2;
    }
  }
}
</style>
